/* Vendor profile media grid (Instagram-style) + Lightbox modal. */
import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { MediaFavoriteHeart } from "@/lib/favorites";

export const Lightbox = ({ items, index, onClose, onPrev, onNext }) => {
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      data-testid="media-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-5 right-5 inline-flex h-11 w-11 items-center justify-center text-white/80 hover:text-white border border-white/20 hover:border-white transition-colors"
      >
        <X size={18} strokeWidth={1.6} />
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            aria-label="Previous"
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center text-white/80 hover:text-white border border-white/20 hover:border-white transition-colors"
          >
            <ChevronLeft size={20} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            aria-label="Next"
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center text-white/80 hover:text-white border border-white/20 hover:border-white transition-colors"
          >
            <ChevronRight size={20} strokeWidth={1.6} />
          </button>
        </>
      )}

      <div
        className="relative max-w-[92vw] max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === "video" ? (
          <video
            key={item.src}
            src={item.src}
            poster={item.poster}
            controls
            autoPlay
            muted
            playsInline
            className="max-w-[92vw] max-h-[88vh] w-auto h-auto"
          />
        ) : (
          <img
            src={item.src}
            alt={item.alt || ""}
            className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain"
          />
        )}
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[11px] tracking-[0.22em] uppercase text-white/60">
        {index + 1} / {items.length}
      </div>
    </div>
  );
};

const MediaGridTile = ({ item, onOpen }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (item.type !== "video" || !videoRef.current) return undefined;
    const el = videoRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) el.play().catch(() => {});
          else el.pause();
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [item.type]);

  return (
    <button
      type="button"
      data-testid={`media-tile-${item.type}`}
      onClick={onOpen}
      className="group relative block w-full overflow-hidden bg-[var(--ld-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--ld-gold)] focus:ring-offset-2 focus:ring-offset-[var(--ld-ivory)]"
    >
      <div className="aspect-square w-full">
        {item.type === "video" ? (
          <video
            ref={videoRef}
            src={item.src}
            poster={item.poster}
            muted
            loop
            playsInline
            preload="metadata"
            className="block w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <img
            src={item.src}
            alt={item.alt || ""}
            loading="lazy"
            className="block w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
          />
        )}
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 explore-tile-vignette"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 explore-tile-gold-wash"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 ring-1 ring-transparent group-hover:ring-[var(--ld-gold)]/55 transition-colors duration-500"
      />

      {item.type === "video" && (
        <div className="pointer-events-none absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-1 bg-black/55 text-white/90 backdrop-blur-sm">
          <Play size={11} strokeWidth={2} fill="currentColor" />
          <span className="text-[10px] tracking-[0.2em] uppercase">Reel</span>
        </div>
      )}
    </button>
  );
};

export const MediaGrid = ({ items, vendor_slug }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const close = () => setOpenIndex(null);
  const prev = () =>
    setOpenIndex((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  const next = () =>
    setOpenIndex((i) => (i === null ? null : (i + 1) % items.length));

  return (
    <>
      <ul
        data-testid="media-grid"
        className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2"
      >
        {items.map((item, i) => (
          <li key={item.src} className="relative">
            <MediaGridTile item={item} onOpen={() => setOpenIndex(i)} />
            {vendor_slug && (
              <MediaFavoriteHeart
                vendor_slug={vendor_slug}
                media_url={item.src}
                media_type={item.type === "video" ? "video" : "image"}
                size={13}
                className="absolute top-2 right-2 h-8 w-8 z-10"
                testid={`grid-media-favorite-${vendor_slug}-${i}`}
              />
            )}
          </li>
        ))}
      </ul>
      {openIndex !== null && (
        <Lightbox
          items={items}
          index={openIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </>
  );
};
